import { useState, useEffect, useReducer } from 'react'
import { useParams, Link } from 'react-router-dom'
import { FaPlusCircle } from 'react-icons/fa'
import { AddButton } from 'components/common/AddButton'
import { AddChecklistModal } from './AddChecklistModal'
import { Checklist } from './Checklist'
import { getChecklists, postChecklist, putChecklist, removeChecklist } from './helpers'
import { useLoading } from 'hooks'
import { reducer, initialState } from './reducer'
import * as ACT from './actions'
import s from './ChecklistsContainer.scss'
import { makeGetRequest, makePostRequest } from 'utils/api'
import Select from 'react-select'

const ChecklistsContainer = ({ entity, module }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [isAddChecklistModalVisible, setIsAddChecklistModalVisible] = useState(false)
  const { id } = useParams()
  const [editedChecklist, setEditedChecklist] = useState(null)
  const [editedItem, setEditedItem] = useState(null)
  const [, withLoadingLocks] = useLoading()
  const [templates, setTemplates] = useState([])

  const fetchChecklists = async () => {
    const url = `/v1/checklist/${entity}/${id}`
    const result = await withLoadingLocks(getChecklists(url, module))
    dispatch({
      type: ACT.SET_CHECKLISTS,
      payload: {
        checklists: result.companyChecklists || result,
        contactsChecklists: result.companyContactChecklist || []
      }
    })
  }

  useEffect(() => {
    ;(async () => {
      if (module === 'leads') return

      const { checkLists } = await withLoadingLocks(makeGetRequest('/v1/checklist/template'))
      setTemplates(checkLists.map(template => ({ value: template.id, label: template.name })))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchChecklists()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    setIsAddChecklistModalVisible(!!editedChecklist)
  }, [editedChecklist])

  useEffect(() => {
    setIsAddChecklistModalVisible(!!editedItem)
  }, [editedItem])

  const toggleAddChecklistModal = () => {
    setIsAddChecklistModalVisible(!isAddChecklistModalVisible)
    setEditedChecklist(null)
    setEditedItem(null)
  }

  const createChecklist = async title => {
    const url = `/v1/checklist/${entity}/${id}`
    const checklist = await withLoadingLocks(postChecklist(url, { title }, module))
    dispatch({
      type: ACT.ADD_CHECKLIST,
      payload: {
        checklist
      }
    })
  }

  const updateChecklist = async title => {
    const url = `/v1/checklist/${editedChecklist.id}`
    const checklist = await withLoadingLocks(putChecklist(url, { title }, module))

    dispatch({
      type: ACT.EDIT_CHECKLIST,
      payload: {
        checklist,
        checklistId: editedChecklist.id,
        contactId: editedChecklist.contactId
      }
    })
  }

  const setChecklisItem = (checklistId, itemId, checklistItem, contactId) => {
    dispatch({
      type: ACT.EDIT_CHECKLIST_ITEM,
      payload: {
        contactId,
        checklistId,
        itemId,
        checklistItem
      }
    })
  }

  const updateItem = async title => {
    const url = `/v1/checklist/item/${editedItem.item.id}`
    const checklistItem = await withLoadingLocks(putChecklist(url, { name: title }, module))
    setChecklisItem(editedItem.checklistId, editedItem.item.id, checklistItem, editedItem.contactId)
  }

  const toggleItem = async (itemId, status, checklistId, contactId) => {
    const url = `/v1/checklist/item/${itemId}`
    const checklistItem = await withLoadingLocks(
      putChecklist(
        url,
        {
          status: status ? 'COMPLETE' : 'INCOMPLETE'
        },
        module
      )
    )
    setChecklisItem(checklistId, itemId, checklistItem, contactId)
  }

  const deleteItem = async (checklistId, itemId, contactId) => {
    const url = `/v1/checklist/${checklistId}/item/${itemId}`
    await withLoadingLocks(removeChecklist(url, module))

    dispatch({
      type: ACT.DELETE_CHECKLIST_ITEM,
      payload: {
        contactId,
        checklistId,
        itemId
      }
    })
  }

  const addItem = async (checklistId, itemName, contactId) => {
    const url = `/v1/checklist/${checklistId}/item`
    const checklistItem = await withLoadingLocks(postChecklist(url, { name: itemName }, module))
    dispatch({
      type: ACT.ADD_CHECKLIST_ITEM,
      payload: {
        contactId,
        checklistId,
        checklistItem
      }
    })
  }

  const deleteChecklist = async (checklistId, contactId) => {
    const url = `/v1/checklist/${checklistId}`
    await withLoadingLocks(removeChecklist(url, module))

    dispatch({
      type: ACT.DELETE_CHECKLIST,
      payload: {
        contactId,
        checklistId
      }
    })
  }

  const createChecklistFromTemplate = async templateId => {
    await makePostRequest(`/v1/checklist/template/${templateId}/${entity}/${id}`)

    fetchChecklists()
  }

  return (
    <>
      {templates.length > 0 && (
        <div className={s.templates}>
          <div>
            <span>Load Checklist Template: </span>
            <Select
              onChange={value => {
                createChecklistFromTemplate(value.value)
              }}
              options={templates}
              className={s.select}
            />
          </div>
        </div>
      )}
      {!!state.contactsChecklists.length && (
        <h6 className={s.title}>Company Specific Checklists</h6>
      )}
      <AddButton onClick={toggleAddChecklistModal}>
        <FaPlusCircle />
        Add Checklist
      </AddButton>
      {state.checklists.length ? (
        state.checklists.map(checklist => (
          <Checklist
            checklist={checklist}
            key={checklist.id}
            toggleItem={toggleItem}
            addItem={addItem}
            deleteItem={deleteItem}
            deleteChecklist={deleteChecklist}
            setEditedChecklist={setEditedChecklist}
            setEditedItem={setEditedItem}
          />
        ))
      ) : state.contactsChecklists.length ? (
        <p>
          No checklists yet for the entire company. See below if you have checklists for individual
          contacts.
        </p>
      ) : (
        <p style={{ textAlign: 'center' }}>No checklists yet. Please add one.</p>
      )}
      {!!state.contactsChecklists.length && (
        <section className={s.contactsChecklists}>
          <h6 className={s.title}>Contact Checklists</h6>
          {state.contactsChecklists.map(
            contactChecklist =>
              !!contactChecklist.contactChecklists.length && (
                <div key={contactChecklist.contactId} className={s.contactChecklistItem}>
                  <Link to={`/contacts/${contactChecklist.contactId}`}>
                    {contactChecklist.contactFullName}
                  </Link>
                  {contactChecklist.contactChecklists.map(checklist => (
                    <Checklist
                      checklist={checklist}
                      key={checklist.id}
                      toggleItem={toggleItem}
                      addItem={addItem}
                      deleteItem={deleteItem}
                      deleteChecklist={deleteChecklist}
                      setEditedChecklist={setEditedChecklist}
                      setEditedItem={setEditedItem}
                      contactId={contactChecklist.contactId}
                    />
                  ))}
                </div>
              )
          )}
        </section>
      )}
      {isAddChecklistModalVisible && (
        <AddChecklistModal
          toggleAddChecklistModal={toggleAddChecklistModal}
          isAddChecklistModalVisible={isAddChecklistModalVisible}
          createChecklist={createChecklist}
          editedChecklist={editedChecklist}
          updateChecklist={updateChecklist}
          editedItem={editedItem}
          updateItem={updateItem}
        />
      )}
    </>
  )
}

export default ChecklistsContainer
